import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faBell, faPlus, faArrowLeft, faSpinner, faPaperPlane,
  faExclamationTriangle, faInfoCircle, faCheckCircle,
  faCalendarDays, faCreditCard, faCog, faUsers, faTrash,
  faDatabase, faExclamationCircle
} from '@fortawesome/free-solid-svg-icons'
import { notificationService } from '../services/notificationService'
import { databaseNotificationService } from '../services/databaseNotificationService'
import { Notification, NotificationStats } from '../types/notifications'
import { Link } from 'react-router-dom'

interface NotificationForm {
  type: Notification['type']
  title: string
  message: string
  priority: Notification['priority']
  category: Notification['category']
  actionUrl: string
  actionText: string
  expiresInDays: number
}

const AdminNotifications: React.FC = () => {
  const [stats, setStats] = useState<NotificationStats | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [databaseAvailable, setDatabaseAvailable] = useState(false)
  const [checkingDatabase, setCheckingDatabase] = useState(true)
  const [formData, setFormData] = useState<NotificationForm>({
    type: 'info',
    title: '',
    message: '',
    priority: 'medium',
    category: 'general',
    actionUrl: '',
    actionText: '',
    expiresInDays: 0
  })

  useEffect(() => {
    checkDatabaseAndLoadStats()

    // Subscribe to localStorage notification changes
    const unsubscribe = notificationService.subscribe(() => {
      loadLocalStorageStats()
    })

    return unsubscribe
  }, [])

  const checkDatabaseAndLoadStats = async () => {
    setCheckingDatabase(true)
    
    // Check if database tables exist
    const dbAvailable = await databaseNotificationService.checkTablesExist()
    setDatabaseAvailable(dbAvailable)
    
    if (dbAvailable) {
      await loadDatabaseStats()
    } else {
      loadLocalStorageStats()
    }
    
    setCheckingDatabase(false)
  }

  const loadDatabaseStats = async () => {
    try {
      const dbStats = await databaseNotificationService.getStats()
      setStats({
        total: dbStats.total,
        unread: dbStats.unread,
        byType: {
          info: dbStats.byType.info || 0,
          success: dbStats.byType.success || 0,
          warning: dbStats.byType.warning || 0,
          error: dbStats.byType.error || 0,
          event: dbStats.byType.event || 0,
          payment: dbStats.byType.payment || 0,
          system: dbStats.byType.system || 0
        },
        byCategory: {
          general: dbStats.byCategory.general || 0,
          events: dbStats.byCategory.events || 0,
          payments: dbStats.byCategory.payments || 0,
          system: dbStats.byCategory.system || 0,
          coordinator: dbStats.byCategory.coordinator || 0,
          registration: dbStats.byCategory.registration || 0
        },
        byPriority: {
          low: dbStats.byPriority.low || 0,
          medium: dbStats.byPriority.medium || 0,
          high: dbStats.byPriority.high || 0,
          urgent: dbStats.byPriority.urgent || 0
        }
      })
    } catch (error) {
      console.error('Error loading database stats:', error)
      loadLocalStorageStats()
    }
  }

  const loadLocalStorageStats = () => {
    const localStats = notificationService.getStats()
    setStats(localStats)
  }

  const handleCreateNotification = async () => {
    if (!formData.title.trim() || !formData.message.trim()) {
      alert('Please fill in title and message')
      return
    }

    if (!databaseAvailable) {
      alert('Database notifications are not available. Please run the notification schema first.')
      return
    }

    setIsSubmitting(true)

    try {
      const notification = {
        type: formData.type,
        title: formData.title.trim(),
        message: formData.message.trim(),
        priority: formData.priority,
        category: formData.category,
        action_url: formData.actionUrl.trim() || undefined,
        action_text: formData.actionText.trim() || undefined,
        expires_at: formData.expiresInDays > 0 
          ? new Date(Date.now() + formData.expiresInDays * 24 * 60 * 60 * 1000).toISOString()
          : undefined
      }

      const result = await databaseNotificationService.createNotification(notification)

      if (result) {
        // Reset form
        setFormData({
          type: 'info',
          title: '',
          message: '',
          priority: 'medium',
          category: 'general',
          actionUrl: '',
          actionText: '',
          expiresInDays: 0
        })

        setShowCreateForm(false)
        alert('Real notification created successfully! All users will see this notification.')
        
        // Reload stats
        await loadDatabaseStats()
      } else {
        alert('Failed to create notification. Please check the console for errors.')
      }

    } catch (error) {
      console.error('Error creating notification:', error)
      alert('Failed to create notification')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClearAllNotifications = async () => {
    if (window.confirm('This will delete ALL notifications for all users. This action cannot be undone. Continue?')) {
      try {
        if (databaseAvailable) {
          const success = await databaseNotificationService.clearAllNotifications()
          if (success) {
            alert('All database notifications cleared!')
            await loadDatabaseStats()
          } else {
            alert('Failed to clear database notifications')
          }
        } else {
          notificationService.clearAllNotifications()
          alert('All localStorage notifications cleared!')
        }
      } catch (error) {
        console.error('Error clearing notifications:', error)
        alert('Failed to clear notifications')
      }
    }
  }

  const getTypeIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success': return faCheckCircle
      case 'warning': return faExclamationTriangle
      case 'error': return faExclamationTriangle
      case 'event': return faCalendarDays
      case 'payment': return faCreditCard
      case 'system': return faCog
      default: return faInfoCircle
    }
  }

  const getTypeColor = (type: Notification['type']) => {
    switch (type) {
      case 'success': return 'text-green-600'
      case 'warning': return 'text-yellow-600'
      case 'error': return 'text-red-600'
      case 'event': return 'text-blue-600'
      case 'payment': return 'text-purple-600'
      case 'system': return 'text-gray-600'
      default: return 'text-blue-600'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="app-page-header">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="app-header-back">
                <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5" />
              </Link>
              <div className="flex items-center space-x-3">
                <FontAwesomeIcon icon={faBell} className="h-6 w-6 text-primary" />
                <h1 className="text-2xl font-bold text-white">Admin Notifications</h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                {showCreateForm ? 'Cancel' : 'Create Notification'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="content-container py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FontAwesomeIcon icon={faBell} className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Notifications</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="h-6 w-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Unread</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.unread}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Urgent</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.byPriority.urgent}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FontAwesomeIcon icon={faUsers} className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">System Wide</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.byCategory.system}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Database Status */}
        {checkingDatabase ? (
          <div className="bg-white rounded-lg shadow mb-6 p-6">
            <div className="flex items-center justify-center">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin h-6 w-6 text-orange-600 mr-3" />
              <span className="text-gray-600">Checking database connection...</span>
            </div>
          </div>
        ) : (
          <div className={`rounded-lg shadow mb-6 p-6 ${databaseAvailable ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
            <div className="flex items-start space-x-3">
              <FontAwesomeIcon 
                icon={databaseAvailable ? faDatabase : faExclamationCircle} 
                className={`h-6 w-6 mt-1 ${databaseAvailable ? 'text-green-600' : 'text-yellow-600'}`} 
              />
              <div className="flex-1">
                <h3 className={`font-semibold ${databaseAvailable ? 'text-green-800' : 'text-yellow-800'}`}>
                  {databaseAvailable ? 'Database Notifications Available' : 'Database Setup Required'}
                </h3>
                <p className={`text-sm mt-1 ${databaseAvailable ? 'text-green-700' : 'text-yellow-700'}`}>
                  {databaseAvailable 
                    ? 'Real notifications will be stored in the database and visible to all users.'
                    : 'To create real notifications that all users can see, you need to run the notification database schema.'
                  }
                </p>
                {!databaseAvailable && (
                  <div className="mt-3 text-sm text-yellow-700">
                    <p className="font-medium">Setup Instructions:</p>
                    <ol className="list-decimal list-inside mt-2 space-y-1">
                      <li>Open your Supabase SQL Editor</li>
                      <li>Run the SQL from <code className="bg-yellow-100 px-1 rounded">notifications-schema.sql</code></li>
                      <li>Refresh this page</li>
                    </ol>
                    <p className="mt-2 text-xs">
                      Without database setup, only localStorage notifications are available.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Create Notification Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {databaseAvailable ? 'Create New Notification' : 'Database Setup Required'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {databaseAvailable 
                  ? 'Send a real notification to all users in the system'
                  : 'Database notifications are not available. Please run the notification schema first.'
                }
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notification Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as Notification['type'] })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="info">Info</option>
                      <option value="success">Success</option>
                      <option value="warning">Warning</option>
                      <option value="error">Error</option>
                      <option value="event">Event</option>
                      <option value="payment">Payment</option>
                      <option value="system">System</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value as Notification['priority'] })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value as Notification['category'] })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="general">General</option>
                      <option value="events">Events</option>
                      <option value="payments">Payments</option>
                      <option value="system">System</option>
                      <option value="coordinator">Coordinator</option>
                      <option value="registration">Registration</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expires in (days) - 0 for no expiration
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="365"
                      value={formData.expiresInDays}
                      onChange={(e) => setFormData({ ...formData, expiresInDays: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Enter notification title"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Enter notification message"
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Action URL (optional)
                    </label>
                    <input
                      type="text"
                      value={formData.actionUrl}
                      onChange={(e) => setFormData({ ...formData, actionUrl: e.target.value })}
                      placeholder="/events or /dashboard"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Action Text (optional)
                    </label>
                    <input
                      type="text"
                      value={formData.actionText}
                      onChange={(e) => setFormData({ ...formData, actionText: e.target.value })}
                      placeholder="View Details"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Preview:</h4>
                <div className="flex items-start space-x-3">
                  <div className={`flex-shrink-0 ${getTypeColor(formData.type)}`}>
                    <FontAwesomeIcon icon={getTypeIcon(formData.type)} className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {formData.title || 'Notification Title'}
                    </p>
                    <p className="text-gray-600 mt-1">
                      {formData.message || 'Notification message will appear here...'}
                    </p>
                    {formData.actionUrl && formData.actionText && (
                      <p className="text-sm text-orange-600 mt-2">
                        {formData.actionText}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateNotification}
                  disabled={isSubmitting || !formData.title.trim() || !formData.message.trim()}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                      Send Notification
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin Actions */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Admin Actions</h3>
            <p className="text-sm text-gray-600 mt-1">
              {databaseAvailable 
                ? 'Manage database notifications for all users'
                : 'Database setup required for real notifications'
              }
            </p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setShowCreateForm(true)}
                className={`p-4 border rounded-lg text-left ${
                  databaseAvailable 
                    ? 'border-orange-200 hover:bg-orange-50' 
                    : 'border-gray-200 bg-gray-50 cursor-not-allowed'
                }`}
                disabled={!databaseAvailable}
              >
                <FontAwesomeIcon 
                  icon={faPlus} 
                  className={`mb-2 ${databaseAvailable ? 'text-orange-600' : 'text-gray-400'}`} 
                />
                <h4 className={`font-medium ${databaseAvailable ? 'text-gray-900' : 'text-gray-500'}`}>
                  Create New Notification
                </h4>
                <p className={`text-sm ${databaseAvailable ? 'text-gray-600' : 'text-gray-400'}`}>
                  {databaseAvailable 
                    ? 'Send a real notification to all users in the system'
                    : 'Database setup required to create notifications'
                  }
                </p>
              </button>

              <Link
                to="/dashboard/alerts"
                className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left block"
              >
                <FontAwesomeIcon icon={faBell} className="text-green-600 mb-2" />
                <h4 className="font-medium text-gray-900">View All Notifications</h4>
                <p className="text-sm text-gray-600">
                  {databaseAvailable 
                    ? 'See all database and localStorage notifications'
                    : 'See all localStorage notifications'
                  }
                </p>
              </Link>

              <button
                onClick={handleClearAllNotifications}
                className="p-4 border border-red-200 rounded-lg hover:bg-red-50 text-left"
              >
                <FontAwesomeIcon icon={faTrash} className="text-red-600 mb-2" />
                <h4 className="font-medium text-red-900">Clear All Notifications</h4>
                <p className="text-sm text-red-600">
                  {databaseAvailable 
                    ? 'Delete all database notifications (irreversible)'
                    : 'Delete all localStorage notifications (irreversible)'
                  }
                </p>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminNotifications 